/**
 * 이상한 방 - 사물함 (순수 DOM)
 *
 * 근거 문서: 로드맵 확정안 3.2절(꾸밈은 규칙 영향 0 · 별명 · 확률 표시) · 3.6절(확률은 상수 하나에서 화면으로)
 *           GLOSSARY 1.5(사물함 - 인벤토리·창고는 쓰지 않는다)
 *
 * ## 이 화면이 하는 일은 셋이다
 *
 * 가진 것을 보여 주고(꾸밈·별명·준비물), 끼우게 하고(꾸밈·별명), 봉투의 확률을 말한다.
 * 셋째가 이 화면의 존재 이유 중 절반이다 - 확률 표시 의무(3.6절)를 지키는 자리가 봉투 연출 하나뿐이면
 * 봉투를 열지 않은 사람은 확률을 볼 곳이 없다. 열기 **전에** 볼 수 있어야 표시다.
 *
 * ## 확률 문자열은 이 파일에 없다
 *
 * `envelopeTable.gradeOddsLabel`이 표에서 만든다. 여기서 숫자를 적으면 표를 고친 날 화면이 거짓말을 한다.
 * 피티 임계도 표에서 읽어 `{rare}`·`{legend}`로 문장에 넣는다.
 *
 * ## 끼우는 규칙은 여기 없다
 *
 * "안 가진 것은 못 끼운다"는 `inventory.equipLook`이 정한다. 이 화면은 가진 것만 버튼으로 만들 뿐이고,
 * 눌린 것을 그대로 바깥에 알린다. 여기서 소유를 한 번 더 검사하면 규칙이 두 벌이 된다.
 *
 * ## 스타일은 `locker.css` + `plateScreen.css`
 *
 * 봉투와 사물함은 같은 입구(단계 선택 화면)에서 열리는 형제 화면이다. 한쪽만 고쳐 두 화면이 다른
 * 게임처럼 보이는 실패를 막으려고 처음에는 `envelope.css` 한 파일에 함께 있었는데(`levelSelect.css`가 선택·안내를
 * 함께 담는 것과 같은 결정), 그 파일이 1000줄을 넘어 갈랐다. 두 화면이 **함께 입는** 바탕(루트·눈썹·제목·버튼 줄·
 * 보조 버튼·포커스 링)은 `plateScreen.css` 한 벌이라 그 실패는 여전히 막혀 있고, 사물함만의 것은 `locker.css`다.
 * 두 파일을 여기서 import 하는 이유: 클래스를 만드는 파일이 그 스타일을 싣는다 - 봉투 화면의 로드에 몰래 기대지 않는다.
 */

import type { EnvelopeState } from '../game/economy/envelope'
import {
  ENVELOPE_TABLE,
  GRADES,
  gradeNameKey,
  gradeOddsLabel,
  type EnvelopeTable,
} from '../game/economy/envelopeTable'
import {
  collectionOf,
  collectionPercent,
  isCollectionComplete,
  lookCollectionIn,
  ownsLook,
  ownsNickname,
  type CollectionCount,
  type Inventory,
} from '../game/economy/inventory'
import { KITS } from '../game/economy/kits'
import { LOOK_SLOTS, looksInSlot, type LookId } from '../game/economy/looks'
import { completionNicknames, getNickname, type NicknameId } from '../game/economy/titles'
import { onLocaleChange, t } from '../i18n'

import { createElement, formatNumber, setBarRatio } from './dom'
import { rewardLabel } from './envelopeCard'
import { createPinsChip } from './pinsChip'
import { attachScrollFade } from './scrollFade'
import { isSettingsOpen } from './settings'

import './plateScreen.css'
import './locker.css'

/* --- 타입 ---------------------------------------------------------------------- */

export type LockerView = Readonly<{
  inventory: Inventory
  envelope: EnvelopeState
  pins: number
  table?: EnvelopeTable
}>

export type LockerHandlers = Readonly<{
  /** 꾸밈을 끼운다. 자리는 꾸밈 정의가 안다(`inventory.equipLook`). */
  onEquipLook: (id: LookId) => void
  /** 별명을 끼운다. `null`은 "별명 없음". */
  onEquipNickname: (id: NicknameId | null) => void
  /** 단계 선택으로. */
  onBack: () => void
}>

export type LockerHandle = Readonly<{
  show: (view: LockerView) => void
  hide: () => void
  isOpen: () => boolean
  dispose: () => void
}>

export type LockerOptions = Readonly<{
  mount: HTMLElement
  handlers: LockerHandlers
}>

/* --- DOM 헬퍼 ------------------------------------------------------------------ */

/**
 * 구획 하나. 몸통을 돌려준다(제목이 아니라) - 부르는 쪽은 받은 것에 목록을 붙이면 된다.
 *
 * `count`를 주면 제목 옆에 그 갈래의 수집 진도가 붙는다. 진도가 붙는 구획은 **모을 수 있는 것**뿐이다
 * (별명 · 꾸밈 자리 다섯). 준비물은 쓰면 없어져 "모았다"가 성립하지 않고, 확률·최근 봉투는 가진 것이
 * 아니다 - 셋에 수를 붙이면 그 수가 무엇의 분모인지 화면이 설명하지 못한다.
 *
 * ## `key`를 준 구획은 접힌다
 *
 * 카탈로그가 스물아홉일 때는 전부 펼쳐 두는 것이 옳았다. 이백아흔이 되자 다 모은 사람의 사물함이
 * **폰 화면 열 개 분량**(5767px)이 되었고, 별명 하나를 갈아 끼우려면 여덟 화면을 지나야 했다.
 * 겹치지도 잘리지도 않으므로 `probeScreen()`은 초록이었다 - 이 고장은 계측기가 못 보는 종류다.
 *
 * 그래서 모을 수 있는 여섯은 접힌 채로 서고, 제목 줄이 곧 목차가 된다(이름 + 진도). 접지 않는 셋은
 * 짧거나(준비물 넷), 접으면 안 되는 것이다 - **확률은 탭 뒤에 숨기지 않는다**(로드맵 3.6절의 표시 의무는
 * "열기 전에 볼 수 있어야 표시다"이고, 한 번의 탭도 그 문장을 약하게 만든다).
 */
function createSection(
  parent: HTMLElement,
  titleText: string,
  count?: CollectionCount,
  collapse?: Readonly<{ key: string; isOpen: (key: string) => boolean; onToggle: (key: string) => void }>,
): HTMLElement {
  const section = createElement('section', 'lk-section')
  const heading = createElement('h2', 'lk-section__title')
  const body = createElement('div', 'lk-section__body')
  const label = createElement('span', 'lk-section__name', titleText)
  const countEl =
    count === undefined
      ? null
      : createElement('span', 'lk-section__count', t('locker.collectionOf', { owned: count.owned, total: count.total }))

  if (collapse === undefined) {
    heading.append(label)
    if (countEl !== null) heading.append(countEl)
  } else {
    const toggle = createElement('button', 'lk-section__toggle')
    toggle.type = 'button'
    body.id = `lk-body-${collapse.key}`
    toggle.setAttribute('aria-controls', body.id)
    const open = collapse.isOpen(collapse.key)
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
    body.hidden = !open
    toggle.append(label)
    if (countEl !== null) toggle.append(countEl)
    toggle.append(createElement('span', 'lk-section__caret'))
    /*
     * 접었다 펴는 것으로 화면을 다시 그리지 않는다. 다시 그리면 스크롤이 맨 위로 튀는데, 아래쪽 구획을
     * 펴려던 사람에게는 그것이 곧 실패다. 대신 이 자리에서 `hidden`만 뒤집고 상태는 바깥에 알린다
     * (언어를 바꿔 다시 그릴 때 그 상태가 필요하다).
     */
    toggle.addEventListener('click', () => {
      const next = body.hidden
      body.hidden = !next
      toggle.setAttribute('aria-expanded', next ? 'true' : 'false')
      collapse.onToggle(collapse.key)
    })
    heading.append(toggle)
  }

  section.append(heading, body)
  parent.append(section)
  return body
}

/** 끼울 수 있는 것 하나. 끼운 것은 노란 판이고 눌러도 아무 일도 없다(이미 그 상태다). */
function createEquipItem(label: string, equipped: boolean, onPick: () => void, ariaPick?: string): HTMLButtonElement {
  const item = createElement('button', equipped ? 'lk-item is-equipped' : 'lk-item', label)
  item.type = 'button'
  item.setAttribute('aria-pressed', equipped ? 'true' : 'false')
  item.setAttribute(
    'aria-label',
    equipped ? `${label}. ${t('locker.equipped')}` : (ariaPick ?? t('locker.equipAria', { name: label })),
  )
  if (!equipped) item.addEventListener('click', onPick)
  return item
}

/**
 * 아직 못 얻은 완주 보상 한 줄. **버튼이 아니라 판이다** - 눌리지 않는 버튼을 두면 사람은 그것을
 * 고장으로 읽는다. 대신 값 칸에 이유("다 모으면")가 적혀 있고, 무엇을 다 모으는지는 머리의 한 줄이 말한다.
 *
 * 얻고 나면 이 함수는 안 불린다 - 그때부터는 자리의 다른 꾸밈과 **똑같은** 버튼이다. 완주 보상만의
 * 구획을 따로 두지 않는 이유가 그것이고, 그렇게 두면 얻은 뒤 같은 것이 화면에 둘 뜬다.
 */
function createLockedPlate(name: string): HTMLElement {
  const plate = createPlate(name, t('locker.completionLocked'))
  plate.classList.add('is-locked')
  return plate
}

/** 끼울 수 없는 정보 한 줄(준비물 수 · 확률 · 최근 봉투). 버튼이 아니다. */
function createPlate(label: string, value: string, grade?: string): HTMLElement {
  const plate = createElement('div', 'lk-plate')
  if (grade !== undefined) plate.dataset.grade = grade
  plate.append(createElement('span', 'lk-plate__label', label), createElement('span', 'lk-plate__value', value))
  return plate
}

/* --- 본체 ---------------------------------------------------------------------- */

/**
 * 사물함을 만들어 `mount`에 붙인다. 처음에는 숨겨져 있다.
 * 상태를 갖지 않는다 - 무엇을 가졌는지도, 끼운 결과도 전부 바깥(`main.ts` · `progress`)의 것이다.
 */
export function createLocker(options: LockerOptions): LockerHandle {
  const root = createElement('div', 'lk-root')
  root.hidden = true
  root.setAttribute('role', 'dialog')
  root.setAttribute('aria-modal', 'true')
  root.setAttribute('aria-labelledby', 'lk-title')

  const head = createElement('header', 'lk-head')
  const eyebrow = createElement('p', 'lk-eyebrow', '')
  const title = createElement('h1', 'lk-title', '')
  title.id = 'lk-title'
  const pinsChip = createPinsChip()
  const summary = createElement('p', 'lk-summary', '')
  /*
   * 수집률. 요약 줄(핀·연 봉투 수)이 "지금까지 무엇을 했나"라면 이 줄은 "얼마나 남았나"다 - 사물함을
   * 여는 이유가 둘이라 줄도 둘이다. 막대는 글자의 사본이 아니라 **한눈에 읽히는 같은 값**이고,
   * 비율은 `setBarRatio` 하나가 심는다(다시 칠하기 규약).
   */
  const collection = createElement('p', 'lk-collection', '')
  const collectionBar = createElement('span', 'lk-collection__bar')
  collectionBar.setAttribute('aria-hidden', 'true')
  const collectionFill = createElement('span', 'lk-collection__fill')
  collectionBar.append(collectionFill)
  /*
   * 완주 보상 안내. 다 모은 사람에게는 감춘다 - 이미 받은 것을 계속 안내하면 그 줄이 화면의 소음이 된다.
   * 이 줄이 필요한 이유는 잠긴 판이 자리마다 흩어져 있기 때문이다: 표식을 6/6 모은 사람이 그 아래의
   * 잠긴 줄을 보면 "다 모았는데 왜 안 열리지"가 된다. 분모가 자리가 아니라 전체라는 것을 여기서 한 번 말한다.
   */
  const completionNote = createElement('p', 'lk-note', '')
  head.append(eyebrow, title, pinsChip.root, summary, collection, collectionBar, completionNote)

  const scroll = createElement('div', 'lk-scroll')
  const stopScrollFade = attachScrollFade(scroll)

  const actions = createElement('div', 'lk-actions')
  const back = createElement('button', 'lk-back', '')
  back.type = 'button'
  actions.append(back)

  root.append(head, scroll, actions)
  options.mount.append(root)

  let lastView: LockerView | null = null

  /*
   * 펼쳐 둔 구획. **저장하지 않는다** - 계정에 실을 값이 아니고, 이 화면을 닫았다 열면 다시 목차로
   * 돌아오는 편이 낫다(무엇을 펴 두었는지는 방금의 의도이지 계정의 사실이 아니다).
   * 무대가 사는 동안만 남으므로 `localStorage`를 아는 파일이 늘지 않는다(`netBoot.ts` 규약).
   */
  const opened = new Set<string>()
  const collapseFor = (key: string) => ({
    key,
    isOpen: (k: string) => opened.has(k),
    onToggle: (k: string) => {
      if (opened.has(k)) opened.delete(k)
      else opened.add(k)
    },
  })

  function table(): EnvelopeTable {
    return lastView?.table ?? ENVELOPE_TABLE
  }

  /** 화면 전체를 한 뷰로 그린다. `show`와 언어 전환이 같은 함수를 쓴다. */
  function paint(view: LockerView): void {
    const { inventory, envelope } = view
    eyebrow.textContent = t('locker.eyebrow')
    title.textContent = t('locker.title')
    back.textContent = t('intro.back')
    pinsChip.set(view.pins)
    summary.textContent = t('locker.summary', { pins: formatNumber(view.pins), opened: envelope.seq })

    // 수집률. 세는 것은 `game/economy`이고 여기는 받은 수를 놓기만 한다 - 분모를 화면이 정하면 그 규칙이 두 곳에 산다.
    const collected = collectionOf(inventory)
    const percent = collectionPercent(collected.all)
    collection.textContent = t('locker.collection', {
      percent,
      owned: collected.all.owned,
      total: collected.all.total,
    })
    setBarRatio(collectionFill, collected.all.total === 0 ? 0 : collected.all.owned / collected.all.total)
    completionNote.textContent = t('locker.completionNote')
    completionNote.hidden = isCollectionComplete(inventory)

    scroll.replaceChildren()

    /* 별명. "별명 없음"도 고를 수 있는 것 하나다 - 뺄 방법이 없으면 한 번 끼운 별명이 영원하다. */
    const nicknames = createSection(scroll, t('locker.nicknames'), collected.nicknames, collapseFor('nicknames'))
    const nickList = createElement('ul', 'lk-items')
    const noneItem = createElement('li', 'lk-items__item')
    noneItem.append(
      createEquipItem(
        t('locker.nicknameNone'),
        inventory.equipped.nickname === null,
        () => options.handlers.onEquipNickname(null),
        // "별명 없음 끼우기"는 말이 안 된다. 뺀다는 동사로 읽어 준다.
        t('locker.unequipNickname'),
      ),
    )
    nickList.append(noneItem)
    for (const id of inventory.nicknames) {
      const li = createElement('li', 'lk-items__item')
      li.append(
        createEquipItem(t(getNickname(id).nameKey), inventory.equipped.nickname === id, () =>
          options.handlers.onEquipNickname(id),
        ),
      )
      nickList.append(li)
    }
    for (const nick of completionNicknames()) {
      if (ownsNickname(inventory, nick.id)) continue
      const li = createElement('li', 'lk-items__item')
      li.append(createLockedPlate(t(nick.nameKey)))
      nickList.append(li)
    }
    nicknames.append(nickList)

    /*
     * 꾸밈. 자리마다 가진 것만 - 기본 꾸밈은 누구나 가진 것이라 언제나 첫 자리에 있고,
     * 완주 보상은 목록의 끝이라 못 얻었으면 잠긴 판으로 맨 뒤에 선다.
     */
    for (const slot of LOOK_SLOTS) {
      const section = createSection(scroll, t(`locker.slot.${slot}`), lookCollectionIn(inventory, slot), collapseFor(slot))
      const list = createElement('ul', 'lk-items')
      for (const look of looksInSlot(slot)) {
        if (!ownsLook(inventory, look.id)) {
          // 못 얻은 것 중 화면에 나오는 것은 완주 보상뿐이다. 안 뽑은 봉투 산출물까지 늘어놓으면
          // 사물함이 "가진 것"이 아니라 도감이 되고, 그건 이 화면이 하기로 한 일이 아니다.
          if (look.source === 'complete') {
            const locked = createElement('li', 'lk-items__item')
            locked.append(createLockedPlate(t(look.nameKey)))
            list.append(locked)
          }
          continue
        }
        const li = createElement('li', 'lk-items__item')
        li.append(
          createEquipItem(t(look.nameKey), inventory.equipped[slot] === look.id, () =>
            options.handlers.onEquipLook(look.id),
          ),
        )
        list.append(li)
      }
      section.append(list)
    }

    /* 준비물. 넷 전부, 0개도 그린다 - 0이 곧 "이런 것이 있다"는 정보다(결과 화면의 0점 줄과 같은 결정). */
    const kits = createSection(scroll, t('locker.kits'))
    const kitList = createElement('div', 'lk-items')
    for (const kit of KITS) {
      kitList.append(createPlate(t(kit.nameKey), t('locker.kitCount', { count: inventory.kits[kit.id] })))
    }
    kits.append(kitList)

    /* 확률. 문자열은 표에서만 온다. 피티 임계도 표의 값이다. */
    const odds = createSection(scroll, t('locker.odds'))
    const oddsList = createElement('div', 'lk-items')
    for (const grade of GRADES) {
      oddsList.append(createPlate(t(gradeNameKey(grade)), gradeOddsLabel(grade, table()), grade))
    }
    odds.append(
      oddsList,
      createElement('p', 'lk-note', t('locker.oddsNote', { rare: table().pity.rareAfter, legend: table().pity.legendAfter })),
    )

    /* 최근 봉투. 새것이 위다. */
    const recent = createSection(scroll, t('locker.recent'))
    if (envelope.log.length === 0) {
      recent.append(createElement('p', 'lk-empty', t('locker.empty')))
    } else {
      const list = createElement('div', 'lk-items')
      for (const draw of [...envelope.log].reverse()) {
        list.append(
          createPlate(
            `${t('envelope.seq', { seq: draw.seq })} ${t(gradeNameKey(draw.grade))}`,
            rewardLabel(draw.reward),
            draw.grade,
          ),
        )
      }
      recent.append(list)
    }
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (root.hidden || isSettingsOpen()) return
    if (event.altKey || event.ctrlKey || event.metaKey) return
    if (event.key !== 'Escape') return
    event.preventDefault()
    options.handlers.onBack()
  }

  const runBack = (): void => options.handlers.onBack()
  back.addEventListener('click', runBack)
  window.addEventListener('keydown', handleKeyDown)

  const stopLocaleWatch = onLocaleChange(() => {
    if (lastView !== null) paint(lastView)
  })

  return {
    show(view: LockerView): void {
      lastView = view
      paint(view)
      root.hidden = false
      back.focus()
    },

    hide(): void {
      if (root.contains(document.activeElement)) {
        const focused = document.activeElement
        if (focused instanceof HTMLElement) focused.blur()
      }
      root.hidden = true
    },

    isOpen: () => !root.hidden,

    dispose(): void {
      stopLocaleWatch()
      stopScrollFade()
      pinsChip.dispose()
      back.removeEventListener('click', runBack)
      window.removeEventListener('keydown', handleKeyDown)
      root.remove()
    },
  }
}
